/**
 * 최종 이미지 파이프라인.
 *
 * 신호 순서 (이 순서를 어기면 톤이 두 번 먹거나 블룸이 LDR 에서 터진다):
 *
 *   HDR 씬 색 + 깊이/노멀
 *     -> GTAO      (조명 계열 스크린 효과)
 *     -> Bloom     (반드시 톤매핑 '전', HDR 에서)
 *     -> Final     (노출 -> ACES 톤매핑 -> 그레이딩 -> 비네트/그레인 -> sRGB 출력)
 *
 * 톤매핑은 Final 패스에서 **딱 한 번** 한다. 그래서 renderer.toneMapping 은
 * NoToneMapping 으로 두고, 컴포저를 못 쓰는 저사양 경로에서만 되돌린다.
 *
 * UI 는 DOM 이라 이 파이프라인을 통과하지 않는다 (별도 보호 전략 불필요).
 */

import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { GTAOPass } from 'three/examples/jsm/postprocessing/GTAOPass.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { DECAL_LAYER } from './decals';

export type Quality = 'high' | 'medium' | 'low';
/** 효과 단독 확인용 뷰 — 튜닝 전에 반드시 존재해야 한다 */
export type PipelineView = 'final' | 'ao' | 'bloom' | 'raw';

/** 최종 패스: 노출/톤매핑/그레이딩/비네트/그레인/출력 변환을 한 번에 소유한다 */
const FinalShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    exposure: { value: 0.82 },
    // 그레이딩: 어두운 저택 — 그림자는 차게, 하이라이트는 등불색으로
    shadowTint: { value: new THREE.Color(0.87, 0.92, 1.03) },
    highlightTint: { value: new THREE.Color(1.06, 0.99, 0.9) },
    contrast: { value: 1.06 },
    /**
     * **대비의 회전축.** 0.5 로 두면 `(c-0.5)*1.06+0.5` 가 0.0283 미만을 전부
     * 음수로 만들고 `max(col,0)` 이 그것을 **정확히 0** 으로 깎는다. 밤 마을
     * 외벽이 통째로 그 구간이라, 채움광을 3.0 까지 올려도 벽 픽셀이 (0,34,91)
     * — R 이 0 인 채로 나왔다. 「문도 없고 벽이 새까맣다」의 진짜 원인이 이것.
     * 실측: B 는 예측 87 / 실측 91 로 맞는데 R 만 죽어 있었다.
     * 밤 화면의 기준 밝기에 맞춰 축을 중간 회색으로 내린다.
     */
    pivot: { value: 0.18 },
    saturation: { value: 0.94 },
    vignette: { value: 0.42 },
    grain: { value: 0.024 },
    time: { value: 0 },
    /** 건물별 그레이딩 (2026-09-14). −1 차갑게 … +1 따뜻하게. 조합센터는 차갑게, 관사는 따뜻하게 */
    warmth: { value: 0 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float exposure;
    uniform vec3 shadowTint;
    uniform vec3 highlightTint;
    uniform float contrast;
    uniform float pivot;
    uniform float saturation;
    uniform float vignette;
    uniform float grain;
    uniform float time;
    uniform float warmth;
    varying vec2 vUv;

    // ACES filmic (Narkowicz 근사) — 톤매핑은 여기서 단 한 번
    vec3 aces(vec3 x) {
      const float a = 2.51, b = 0.03, c = 2.43, d = 0.59, e = 0.14;
      return clamp((x * (a * x + b)) / (x * (c * x + d) + e), 0.0, 1.0);
    }

    void main() {
      vec3 hdr = texture2D(tDiffuse, vUv).rgb * exposure;
      vec3 col = aces(hdr);

      // 그레이딩: 휘도로 그림자/하이라이트를 갈라 각각 다른 색을 얹는다
      float luma = dot(col, vec3(0.2126, 0.7152, 0.0722));
      vec3 tint = mix(shadowTint, highlightTint, smoothstep(0.15, 0.75, luma));
      // 건물별 그레이딩: 따뜻하면 하이라이트를 주황 쪽으로, 차가우면 그림자를 청록 쪽으로 — 아주 옅게
      tint *= mix(vec3(1.0), vec3(1.05, 0.99, 0.93), max(warmth, 0.0)) * mix(vec3(1.0), vec3(0.96, 0.99, 1.05), max(-warmth, 0.0));
      col *= tint;
      col = (col - pivot) * contrast + pivot;
      col = mix(vec3(luma), col, saturation);

      // 비네트 — 1인칭 실내에서 시선을 화면 중앙으로 모은다
      vec2 d = vUv - 0.5;
      col *= 1.0 - vignette * dot(d, d) * 2.0;

      /**
       * 필름 그레인 — 어두운 부분의 밴딩을 깨준다.
       *
       * **감마 뒤에 얹어야 한다.** 선형값에 그대로 더했더니 뒤따르는 sRGB
       * 인코딩이 어두운 구간을 5~6배로 펴면서 잡티도 같이 폈다. 0.024 짜리
       * 그레인이 밤 벽에서 ±12/255 로 부풀어 **소금후추**가 됐고, 거리가
       * 멀어도 화면 공간이라 그대로 남았다. 여기서 한 번 감마를 씌워
       * 눈에 보이는 값에 더한 뒤 되돌린다.
       *
       * 세기도 어두운 쪽에서 **덜** 키운다 — 원래 (1-luma*0.7) 로 어두울수록
       * 키웠는데, 그게 위 문제와 겹쳐 두 배로 터졌다.
       */
      float n = fract(sin(dot(vUv * (1.0 + time), vec2(12.9898, 78.233))) * 43758.5453);
      vec3 disp = pow(max(col, 0.0), vec3(0.4545));
      disp += (n - 0.5) * grain * (1.0 - luma * 0.35);
      col = pow(max(disp, 0.0), vec3(2.2));

      /**
       * **sRGB 인코딩을 직접 한다.** 전에는 colorspace_fragment 청크였는데
       * 그 청크는 「화면에 그릴 때만」 인코딩한다 — SMAA 를 뒤에 붙이면서 이 패스가
       * 렌더타겟으로 가게 되자 인코딩이 통째로 빠져 화면 전체가 선형값 그대로,
       * 즉 한 단 어둡게 나왔다(별동 정면 컷이 남색으로 가라앉았다). SMAA 셰이더에는
       * 색공간 변환이 없으므로 여기서 인코딩한 값이 그대로 화면에 간다.
       */
      vec3 oc = max(col, 0.0);
      vec3 lo = oc * 12.92;
      vec3 hi = 1.055 * pow(oc, vec3(1.0 / 2.4)) - 0.055;
      gl_FragColor = vec4(mix(lo, hi, step(vec3(0.0031308), oc)), 1.0);
    }
  `,
};

export class Pipeline {
  composer: EffectComposer | null = null;
  private renderPass!: RenderPass;
  private gtao: GTAOPass | null = null;
  private bloom: UnrealBloomPass | null = null;
  private final!: ShaderPass;
  private view: PipelineView = 'final';
  quality: Quality;

  constructor(
    private renderer: THREE.WebGLRenderer,
    private scene: THREE.Scene,
    private camera: THREE.PerspectiveCamera,
    quality: Quality = 'high',
  ) {
    this.quality = quality;
    this.build();
  }

  private build() {
    const { renderer, scene, camera } = this;
    const w = renderer.domElement.width;
    const h = renderer.domElement.height;

    this.dispose();

    // 데칼(접지 그림자·빛 웅덩이·빛 기둥)은 전용 레이어에 있다. 본 화면에는
    // 나와야 하므로 카메라에서 켜 두고, 아래에서 GTAO 프리패스 동안만 끈다
    camera.layers.enable(DECAL_LAYER);

    if (this.quality === 'low') {
      // 컴포저 없이 직접 렌더 — 이 경로에서만 렌더러가 톤매핑을 소유한다
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 0.95;
      this.composer = null;
      return;
    }

    renderer.toneMapping = THREE.NoToneMapping;

    // HDR 을 유지해야 블룸이 실제 밝은 부분만 잡는다.
    //
    // samples(MSAA)는 0 이다. EffectComposer 는 이 타겟을 clone() 해서 읽기/쓰기
    // 버퍼로 쓰고 clone 은 samples 까지 복사하므로(RenderTarget.copy), samples 4 는
    // **MSAA 4배 half-float 타겟 두 벌**이 된다 — 픽셀당 112바이트, 4K 에서 1.16GB.
    // 게다가 뒤에 GTAO/블룸/Final 이 붙어 결과를 다시 리샘플하므로 MSAA 이득은
    // 대부분 사라진다. 계단이 거슬리면 MSAA 를 되살리는 대신 렌더 픽셀 예산
    // (adaptive.ts 의 STEPS)을 한 단 올리는 쪽이 같은 비용에서 더 낫다.
    const target = new THREE.WebGLRenderTarget(w, h, {
      type: THREE.HalfFloatType,
      samples: 0,
      // 본 패스의 깊이를 GTAO 가 그대로 읽는다(아래). clone 된 두 번째 버퍼도 제 깊이 텍스처를 갖는다
      depthTexture: this.quality === 'high' ? new THREE.DepthTexture(w, h) : undefined,
    });
    const composer = new EffectComposer(renderer, target);
    composer.setSize(w, h);

    this.renderPass = new RenderPass(scene, camera);
    composer.addPass(this.renderPass);

    if (this.quality === 'high') {
      // 실내 접촉 그림자 — 가구/시신이 바닥에 '놓여 있게' 만드는 결정적 신호
      const gtao = new GTAOPass(scene, camera, w, h);
      gtao.output = GTAOPass.OUTPUT.Default;
      /**
       * 세기는 `updateGtaoMaterial` 의 scale 이 아니라 `blendIntensity` 가 담당한다.
       *
       * **2.2 는 너무 셌다.** 기본값이 1.0 인데 두 배를 넘겨 놓으니 접촉 그림자가
       * 아니라 검댕처럼 눌어붙었고, 샘플 노이즈까지 같이 증폭돼 물건 표면에
       * 금색 알갱이가 끓었다 — 촛불 색이 그 노이즈에 실린 것이다.
       *
       * 더 헷갈렸던 것은 **시간이 지나면 사라진다**는 점이었다. GTAO 는 `high`
       * 화질에서만 켜지는데, 적응형 화질이 프레임을 보고 등급을 내리면서 패스가
       * 통째로 꺼진다. 그래서 「처음에만 이상한 그래픽」으로 보였다.
       */
      gtao.blendIntensity = 1.15;
      // 화면공간 반경: 실내에서 카메라가 벽에 붙어도 가려짐 폭이 일정하게 유지된다
      gtao.updateGtaoMaterial({
        radius: 0.25,
        distanceExponent: 1.0,
        thickness: 1.4,
        distanceFallOff: 1.0,
        samples: 16,
        screenSpaceRadius: true,
      });
      // 노이즈 제거를 세게 건다. 샘플을 8에서 16으로 올리고 반경을 넓히면
      // 알갱이가 눈에 띄게 줄어든다 — GTAO 자체 샘플을 늘리는 것보다 싸다
      gtao.updatePdMaterial({ lumaPhi: 10, depthPhi: 2.2, normalPhi: 3.2, radius: 4, samples: 16 });
      /**
       * **깊이는 본 패스 것을 쓴다** (2026-09-26). GTAOPass 는 기본으로 씬 전체를 법선·깊이용으로 **한 번 더 그린다** —
       * 조합 홀에서 프레임당 draw call 이 1,256 → 2,546 으로 두 배였고 맥북 M2 Pro 에서 53fps 였다(해상도를 반으로
       * 줄여도 그대로 — draw call 병목). 깊이를 넘기면 G 버퍼를 안 굽고 법선은 깊이에서 복원한다.
       * 데칼은 depthWrite:false 라(decals.ts) 본 패스 깊이에 안 남는다 — 예전 「구덩이」가 다시 생기지 않는다.
       * 컴포저의 두 버퍼 중 이번 프레임에 본 패스가 그린 쪽(readBuffer)의 깊이를 프레임마다 물린다.
       */
      gtao.setGBuffer(target.depthTexture!);
      (gtao as unknown as { normalRenderTarget?: THREE.WebGLRenderTarget }).normalRenderTarget?.dispose();   // 안 쓰는 G 버퍼
      /**
       * **GTAO 에서 데칼을 빼는 자리.**
       *
       * `GTAOPass.render` 는 안에서 `scene.overrideMaterial` 을 걸고 깊이·법선을
       * 한 번 더 굽는다. 오버라이드는 재질을 통째로 갈아치우므로 `transparent`
       * 도 `depthWrite:false` 도 남지 않는다 — 바닥에서 12mm 뜬 빛 웅덩이 판이
       * 도로를 깊이 버퍼에서 덮어 버렸고, 그 결과가 **환상로·대로의 rgb(0,0,0)
       * 구덩이**였다. 본 화면은 이미 `RenderPass` 가 그린 뒤이므로, 이 패스가
       * 도는 동안만 카메라에서 레이어를 끄면 된다.
       */
      const gtaoRender = gtao.render.bind(gtao);
      gtao.render = ((...args: Parameters<typeof gtaoRender>) => {
        const depth = args[2].depthTexture;
        if (depth) { gtao.gtaoMaterial.uniforms.tDepth.value = depth; gtao.pdMaterial.uniforms.tDepth.value = depth; }
        camera.layers.disable(DECAL_LAYER);
        try {
          return gtaoRender(...args);
        } finally {
          camera.layers.enable(DECAL_LAYER);
        }
      }) as typeof gtao.render;
      this.gtao = gtao;
      composer.addPass(gtao);
    }

    // 블룸은 톤매핑 전 HDR 에서
    const bloom = new UnrealBloomPass(new THREE.Vector2(w, h), 0.22, 0.5, 1.4);
    this.bloom = bloom;
    composer.addPass(bloom);

    this.final = new ShaderPass(FinalShader);
    composer.addPass(this.final);

    /**
     * **처마선이 계단이었다.** 별동 박공 정면 컷에서 지붕 사선이 10px 단위로
     * 층졌다 — Chris 가 「디자인이 구리다」고 한 컷의 첫 번째 원인. MSAA 는
     * 위 주석대로 못 켠다(HalfFloat 타겟 두 벌). 대신 sRGB 출력 **뒤에** SMAA 를
     * 한 장 얹는다. 형태학적 AA 라 톤매핑이 끝난 LDR 에서 돌아야 하고, 그래서
     * Final 다음이다. 비용은 풀해상도 3패스, 그 중 둘은 아주 가벼운 edge/blend
     * 계산이다.
     */
    const smaa = new SMAAPass();
    smaa.renderToScreen = true;
    composer.addPass(smaa);

    this.composer = composer;
    this.applyView();
  }

  setQuality(q: Quality) {
    if (q === this.quality) return;
    this.quality = q;
    this.build();
  }

  /** 건물별 그레이딩. 부드럽게 따라간다(방을 건널 때 톤이 툭 바뀌지 않게) */
  private warmTarget = 0;
  setWarmth(w: number) { this.warmTarget = Math.max(-1, Math.min(1, w)); }
  setView(v: PipelineView) {
    this.view = v;
    this.applyView();
  }

  getView() {
    return this.view;
  }

  private applyView() {
    if (!this.composer) return;
    if (this.gtao) {
      this.gtao.output = this.view === 'ao' ? GTAOPass.OUTPUT.AO : GTAOPass.OUTPUT.Default;
      this.gtao.enabled = this.view !== 'raw';
    }
    if (this.bloom) this.bloom.enabled = this.view === 'final' || this.view === 'bloom';
    // 'bloom' 뷰는 블룸 기여만 보기 위해 그레이딩을 끈다
    this.final.enabled = this.view !== 'bloom';
    if (this.view === 'bloom' && this.bloom) this.bloom.renderToScreen = true;
  }

  setSize(w: number, h: number) {
    this.composer?.setSize(w, h);
    this.gtao?.setSize(w, h);
    this.bloom?.setSize(w, h);
  }

  render(dt: number) {
    if (this.composer) {
      this.final.uniforms.time.value += dt;
      const u = this.final.uniforms.warmth; u.value += (this.warmTarget - u.value) * Math.min(1, dt * 1.5);
      this.composer.render(dt);
    } else {
      this.renderer.render(this.scene, this.camera);
    }
  }

  /** 지각 역할별 파라미터 — 튜닝은 여기만 만진다 */
  grade(p: Partial<{ exposure: number; contrast: number; saturation: number; vignette: number; grain: number }>) {
    if (!this.composer) return;
    for (const [k, v] of Object.entries(p)) {
      const u = this.final.uniforms[k as keyof typeof this.final.uniforms];
      if (u && typeof v === 'number') u.value = v;
    }
  }

  bloomParams(p: Partial<{ strength: number; radius: number; threshold: number }>) {
    if (!this.bloom) return;
    if (p.strength !== undefined) this.bloom.strength = p.strength;
    if (p.radius !== undefined) this.bloom.radius = p.radius;
    if (p.threshold !== undefined) this.bloom.threshold = p.threshold;
  }

  dispose() {
    // **EffectComposer.dispose() 는 패스를 정리하지 않는다.** renderTarget1/2 와
    // copyPass 만 버리고 this.passes 는 순회하지 않는다 (three r185 소스 확인).
    // 패스가 들고 있는 렌더타겟은 여기서 직접 버려야 GPU 에서 사라진다.
    //
    // 이걸 빼먹고 있었다. 실측: high<->medium 을 왕복시키면
    // renderer.info.memory.textures 가 45 -> 73 -> 101 -> 129 -> 157 로
    // **왕복 1회당 28장씩 늘고 회수는 0** 이었다 (GTAO 렌더타겟 4 + 노이즈 2,
    // 블룸 mip 11 …). WebGL 리소스는 GC 로 사라지지 않는다 — deleteTexture 를
    // 부르는 건 Texture.dispose() 뿐이다.
    //
    // 적응형 화질이 15초 주기로 오르내리던 것과 겹쳐(adaptive.ts 참고) 4K 에서는
    // 시간당 50GB 규모로 VRAM 을 먹었다. "오래 하면 갑자기 렉이 심해진다" 의 정체.
    this.gtao?.dispose();
    this.bloom?.dispose();
    this.final?.dispose();
    this.renderPass?.dispose();
    this.composer?.dispose();
    this.gtao = null;
    this.bloom = null;
    this.composer = null;
  }
}
